/* eslint-disable no-unused-vars */
/* eslint-disable promise/param-names */
import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './index.css'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/store.ts'
import { scrollToSection } from '../../../helpers/index.tsx'
import { LogoTransparent } from '../../atom/logo-transparent/index.tsx'
import LanguageSwitcher from '../../molecule/languageSwitcher/index.tsx'
import { isValidLanguage } from '../../../types/i18n.ts'

interface Props {
	scrollToContact?: () => void
}

const Navbar : React.FC<Props> = ({
	scrollToContact
}) => {
	const { t, i18n } = useTranslation()
	const { windowWidth } = useSelector((state: RootState) => state.appData)

	const location = useLocation()
	const navigate = useNavigate()

	const navbarDiv = useRef<HTMLDivElement | null>(null)

	const [goToPath, setGoToPath] = useState<string | null>(null)

	const containerRef = useRef<HTMLDivElement>(null)

	// Get the language prefix for links
	const getLangPrefix = () => {
		const lang = i18n.language
		return lang === 'hr' ? '' : `/${lang}`
	}

	const closeNavMenu = async () => {
		const navMenu = document.querySelector('.navbar-menu')
		if (navMenu) {
			navMenu.classList.add('navbar-menu-closed')
			navMenu.classList.remove('navbar-menu-opened')
			const navMenuLinks = document.querySelector('.navbar-menu-content')

			setTimeout(() => {
				if (navMenuLinks) {
					for (let i = 0; i < navMenuLinks.children.length; i++) {
						navMenuLinks.children[i].classList.remove('link-nav-menu-visible')
					}
				}
			}, 400)
		}
	}

	const openNavMenu = async () => {
		const navMenu = document.querySelector('.navbar-menu')

		if (navMenu) {
			navMenu.classList.remove('navbar-menu-closed')
			navMenu.classList.add('navbar-menu-opened')

			const navMenuLinks = document.querySelector('.navbar-menu-content')
			navMenu.classList.remove('navbar-menu-hidden')
			navMenu.classList.add('navbar-menu-visible')

			const timer = (ms: number) => new Promise(res => setTimeout(res, ms))

			if (navMenuLinks) {
				for (let i = 0; i < navMenuLinks.children.length; i++) {
					navMenuLinks.children[i].classList.add('link-nav-menu-visible')
					await timer(200)
				}
			}
		}
	}

	useEffect(() => {
		const navbar = document.querySelector('.navbar-div')

		const path = window.location.pathname
		const pathParts = path.split('/').filter(Boolean)
		const isHome = path === '/' || (pathParts.length === 1 && isValidLanguage(pathParts[0]))

		if (isHome) {
			window.addEventListener('scroll', () => {
				const lastKnownScrollPosition = window.scrollY

				if (lastKnownScrollPosition >= 100 && navbar) {
					navbar.classList.add('sticky')
				} else if (lastKnownScrollPosition <= 100 && navbar) {
					navbar.classList.remove('sticky')
				}
			})
		} else {
			if (navbar) {
				navbar.classList.add('nav-transparent')
			}
		}
	}, [])

	const isActive = (pathName: string) => {
		if (location.pathname === pathName) {
			closeNavMenu()
			window.scrollTo({ top: 0 })
			setGoToPath(null)
		}
	}

	useEffect(() => {
		if (goToPath) {
			isActive(goToPath)
		}
	}, [goToPath])

	useEffect(() => {
		setTimeout(() => {
			navItemActive(location.pathname)
		}, 20)
	}, [location])

	const navItemActive = (path: string) => {
		// Remove active class from all items first
		document.querySelectorAll('.links-container > div').forEach(el => {
			el.classList.remove('active')
		})

		const langPrefix = getLangPrefix()
		const basePath = path.replace(langPrefix, '') || '/'

		switch (basePath) {
			case '/':
				document.querySelector('#home')?.parentElement?.classList.add('active')
				break
			case '/price':
				document.querySelector('#price')?.parentElement?.classList.add('active')
				break
			case '/demo':
				document.querySelector('#demo')?.parentElement?.classList.add('active')
				break
		}
	}

	const homePath = getLangPrefix() || '/'
	const demoPath = `${getLangPrefix()}/demo`
	const pricePath = `${getLangPrefix()}/price`
	const plannerPath = `${getLangPrefix()}/weddingPlanner`

	return (
		<div className='navbar-div'>
			<div ref={containerRef} className='navbar-container'>
				{
					windowWidth <= 1220
						? <div className='navbar-container-small'>
							<Link className='navbar-container-logo-small' to={homePath} onClick={() => setGoToPath(homePath)}>
								<LogoTransparent />
							</Link>
							<button className='navbar-open-btn' onClick={openNavMenu}>
								<span className='hamburger-new-dot'></span>
								<svg className='hamburger-icon' width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M1 1H17M1 7H17M1 13H17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
								</svg>
								<span className='hamburger-text'>{t('nav.menu')}</span>
							</button>

							<div className="navbar-menu navbar-menu-closed">

								<button className="navbar-menu-close" onClick={() => closeNavMenu()}>
									<svg className='close-icon' width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M1 1L13 13M1 13L13 1" stroke="#282828" strokeWidth="2" strokeLinecap="round"/>
									</svg>
									<span className='close-text'>{t('nav.close')}</span>
								</button>

								<div className="navbar-menu-content">
									<div className="link link-nav-menu">
										<Link id='drawer-home' to={homePath} onClick={() => setGoToPath(homePath)}>
											{t('nav.home')}
										</Link>
									</div>

									<div className="link link-nav-menu">
										<Link id='drawer-demo' to={demoPath} onClick={() => {
											closeNavMenu()
										}}>
											{t('nav.demo')}
										</Link>
									</div>

									<div className="link link-nav-menu">
										<Link id='drawer-price' to={pricePath} onClick={() => {
											closeNavMenu()
										}}>
											{t('nav.price')}
										</Link>
									</div>

									<div className='link link-nav-menu'>
										<Link to="" onClick={() => {
											closeNavMenu()
											scrollToSection('contact')
										}}>
											{t('nav.contact')}
										</Link>
									</div>

									<div className='link link-nav-menu nav-link-with-badge-mobile'>
										<Link to={plannerPath} onClick={() => closeNavMenu()}>
											{t('nav.weddingPlanner')}
										</Link>
										<span className='nav-new-badge'>{t('nav.new')}</span>
									</div>

									<div className='link link-nav-menu language-switcher-mobile'>
										<LanguageSwitcher />
									</div>
								</div>
							</div>
						</div>
						: <div ref={navbarDiv} className='navbar-large'>
							<Link className='navbar-container-logo' to={homePath} onClick={() => setGoToPath(homePath)}>
								<LogoTransparent />
							</Link>

							<div className='links-container'>
								<div>
									<Link id="home" to={homePath} onClick={() => setGoToPath(homePath)}>
										{t('nav.home')}
									</Link>
								</div>

								<div>
									<Link id="demo" to={demoPath}>
										{t('nav.demo')}
									</Link>
								</div>

								<div>
									<Link id="price" to={pricePath}>
										{t('nav.price')}
									</Link>
								</div>

								<div>
									<Link to='' onClick={() => {
										const currentPath = location.pathname
										const pathParts = currentPath.split('/').filter(Boolean)
										const isHome = currentPath === '/' || (pathParts.length === 1 && isValidLanguage(pathParts[0]))

										if (!isHome) {
											navigate(homePath)
										}

										setTimeout(() => {
											scrollToSection('contact')
										}, 400)
									}}>
										{t('nav.contact')}
									</Link>
								</div>

								<div className='nav-link-with-badge'>
									<Link to={plannerPath}>
										{t('nav.weddingPlanner')}
									</Link>
									<span className='nav-new-badge'>{t('nav.new')}</span>
								</div>
							</div>

							<LanguageSwitcher />
						</div>
				}
			</div>
		</div>
	)
}

export default Navbar
